import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import spformsApi from '../lib/spformsApi';
import { executeRecaptchaEnterprise } from '../lib/recaptchaEnterprise';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle } = FiIcons;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaConfig, setRecaptchaConfig] = useState(null);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const publicSettings = await spformsApi.settingsGetPublic();

        setRecaptchaConfig({
          enabled: publicSettings.recaptcha_enterprise_enabled,
          site_key: publicSettings.recaptcha_enterprise_site_key,
          action: publicSettings.recaptcha_enterprise_action_login || 'login',
        });
      } catch (e) {
        console.warn('Failed to load public security settings', e);
      }
    }

    fetchConfig();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isBlockingRecaptchaError = (err) => {
    return (
      err?.message === 'RECAPTCHA_EXECUTE_FAILED' ||
      err?.message === 'RECAPTCHA_SCRIPT_LOAD_FAILED' ||
      err?.message === 'RECAPTCHA_TOKEN_MISSING'
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage('');
    setLoading(true);

    let recaptchaToken = null;
    let recaptchaAction = null;

    try {
      if (recaptchaConfig) {
        try {
          const result = await executeRecaptchaEnterprise(
            recaptchaConfig.action,
            recaptchaConfig
          );

          if (result.status === 'OK') {
            recaptchaToken = result.token;
            recaptchaAction = result.action;
          }
        } catch (err) {
          if (isBlockingRecaptchaError(err)) {
            throw new Error(
              'Security verification failed. Please refresh the page and try again.'
            );
          }
        }
      }

      await login({
        ...form,
        recaptcha_token: recaptchaToken,
        recaptcha_action: recaptchaAction,
      });

      navigate('/intake-groups', { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-indigo-700">SportikoForms</h1>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to manage intake groups, registrations, participants, and VOON exports.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="admin@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter password"
              required
            />
          </div>

          {errorMessage ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} />
              {errorMessage}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition ${
              loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {loading ? 'Verifying...' : 'Sign in'}
          </button>
        </form>

        {recaptchaConfig?.enabled === 'true' ? (
          <p className="mt-6 text-[10px] text-center text-gray-400 leading-relaxed">
            This site is protected by reCAPTCHA Enterprise and the Google <br />
            <a
              href="https://policies.google.com/privacy"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>{' '}
            and
            <a
              href="https://policies.google.com/terms"
              className="underline mx-1"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            apply.
          </p>
        ) : null}
      </div>
    </div>
  );
}